import type { PortfolioLive } from '@shared/portfolio'
import type { GroupDetail, GroupRecord } from '@shared/groups'
import type { TicketRecord } from '@shared/orders'
import type {
  AppSettings,
  AuthStatus,
  Chat,
  ChatMode,
  ChatMessage,
  ExtendedQuote,
  LiveQuote,
  Memory,
  MemoryScope,
  MessageHit,
  OhlcBar,
  PortfolioSnapshot,
  Position,
  Project,
  Quote,
  SearchResult,
  SessionUser,
  TradeSide,
  Transaction,
  User,
  XaiBalance,
  TechnicalAnalysis,
  CompanyProfile,
  Task,
  TaskRun,
  TaskLimits,
  TaskInput,
  SchedulePreview,
  UserEvent,
} from '@shared/types'
import type { Timeframe } from '@shared/chart-spec'
import type { ContextUsage } from '@shared/compaction'
import type { BrokerMode, BrokerSnapshot, BrokerStatus, PlaceOrderRequest } from '@shared/broker'
import type { WorkerDetail, WorkerLimits, WorkerRecord } from '@shared/worker'
import type { JournalStats, JournalTrade, TradeExcursion, WhatIfSummary, WhatIfTrade } from '@shared/journal'
import type { WatcherDetail, WatcherRecord, WatcherStatus } from '@shared/watch'

async function j<T>(res: Response): Promise<T> {
  if (res.status === 401 && !res.url.includes('/api/auth/')) {
    window.dispatchEvent(new CustomEvent('lb-unauthorized'))
  }
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`
    try {
      const body = await res.json()
      if (body?.error) msg = body.error
      else if (Array.isArray(body?.errors) && body.errors.length) msg = body.errors.join(' · ')
    } catch {
      /* non-json error */
    }
    throw new Error(msg)
  }
  return (await res.json()) as T
}

const jsonInit = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { 'content-type': 'application/json' },
  body: JSON.stringify(body),
})

export interface Health {
  ok: boolean
  xai: boolean
  eodhd: boolean
  model: string
}

export const api = {
  health: () => fetch('/api/health').then((r) => j<Health>(r)),

  authStatus: () => fetch('/api/auth/status').then((r) => j<AuthStatus>(r)),
  setup: (b: { username: string; password: string; name?: string }) =>
    fetch('/api/auth/setup', jsonInit('POST', b)).then((r) => j<{ user: SessionUser }>(r)),
  login: (b: { username: string; password: string }) =>
    fetch('/api/auth/login', jsonInit('POST', b)).then((r) => j<{ user: SessionUser }>(r)),
  logout: () => fetch('/api/auth/logout', { method: 'POST' }).then((r) => j<{ ok: boolean }>(r)),

  listUsers: () => fetch('/api/admin/users').then((r) => j<User[]>(r)),
  createUser: (b: { username: string; password: string; name?: string; role?: string }) =>
    fetch('/api/admin/users', jsonInit('POST', b)).then((r) => j<User>(r)),
  patchUser: (
    id: string,
    b: { tokenLimit?: number | null; taskDailyLimit?: number | null; taskOccasionalLimit?: number | null; maxFiles?: number | null },
  ) => fetch(`/api/admin/users/${id}`, jsonInit('PATCH', b)).then((r) => j<User>(r)),
  deleteUser: (id: string) =>
    fetch(`/api/admin/users/${id}`, { method: 'DELETE' }).then((r) => j<{ ok: boolean }>(r)),
  xaiBalance: (force = false) =>
    fetch(`/api/admin/xai-balance${force ? '?force=1' : ''}`).then((r) => j<XaiBalance>(r)),

  getSettings: () => fetch('/api/settings').then((r) => j<AppSettings>(r)),
  saveSettings: (patch: Partial<AppSettings>) =>
    fetch('/api/settings', jsonInit('POST', patch)).then((r) => j<AppSettings>(r)),

  listChats: () => fetch('/api/chats').then((r) => j<Chat[]>(r)),
  createChat: (title?: string, projectId?: string | null) =>
    fetch('/api/chats', jsonInit('POST', { title, projectId })).then((r) => j<Chat>(r)),
  getChat: (id: string) =>
    fetch(`/api/chats/${id}`).then((r) => j<{ chat: Chat; messages: ChatMessage[] }>(r)),
  compactChat: (id: string, focus?: string) =>
    fetch(`/api/chats/${id}/compact`, jsonInit('POST', { focus })).then((r) =>
      j<{ ok: boolean; compactedAt: number; context: ContextUsage | null }>(r),
    ),
  patchChat: (id: string, patch: { title?: string; pinned?: boolean; projectId?: string | null }) =>
    fetch(`/api/chats/${id}`, jsonInit('PATCH', patch)).then((r) => j<Chat>(r)),
  deleteChat: (id: string) =>
    fetch(`/api/chats/${id}`, { method: 'DELETE' }).then((r) => j<{ ok: boolean }>(r)),
  setChatTools: (id: string, skillIds: string[] | null, mcpIds: string[] | null) =>
    fetch(`/api/chats/${id}/tools`, jsonInit('PATCH', { skillIds, mcpIds })).then((r) => j<{ ok: boolean }>(r)),
  setChatMode: (id: string, mode: ChatMode | null) =>
    fetch(`/api/chats/${id}/mode`, jsonInit('PATCH', { mode })).then((r) => j<{ ok: boolean }>(r)),
  setChatModel: (id: string, model: string | null) =>
    fetch(`/api/chats/${id}/model`, jsonInit('PATCH', { model })).then((r) => j<{ ok: boolean }>(r)),
  setChatTrading: (id: string, enabled: boolean) =>
    fetch(`/api/chats/${id}/trading`, jsonInit('PATCH', { enabled })).then((r) => j<{ ok: boolean }>(r)),

  listProjects: () => fetch('/api/projects').then((r) => j<Project[]>(r)),
  createProject: (name?: string) =>
    fetch('/api/projects', jsonInit('POST', { name })).then((r) => j<Project>(r)),
  patchProject: (id: string, patch: { name?: string; instructions?: string }) =>
    fetch(`/api/projects/${id}`, jsonInit('PATCH', patch)).then((r) => j<Project>(r)),
  deleteProject: (id: string) =>
    fetch(`/api/projects/${id}`, { method: 'DELETE' }).then((r) => j<{ ok: boolean }>(r)),
  searchAll: (q: string) =>
    fetch(`/api/search?q=${encodeURIComponent(q)}`).then((r) => j<MessageHit[]>(r)),

  listMemory: (scope: MemoryScope = 'global', scopeId?: string | null) => {
    const p = new URLSearchParams({ scope })
    if (scopeId) p.set('scopeId', scopeId)
    return fetch(`/api/memory?${p}`).then((r) => j<Memory[]>(r))
  },
  saveMemory: (m: {
    scope?: MemoryScope
    scopeId?: string | null
    kind: string
    label: string
    value: unknown
  }) => fetch('/api/memory', jsonInit('POST', m)).then((r) => j<Memory>(r)),
  deleteMemory: (id: string) =>
    fetch(`/api/memory/${id}`, { method: 'DELETE' }).then((r) => j<{ removed: number }>(r)),

  getPortfolio: () =>
    fetch('/api/portfolio').then((r) =>
      j<{ positions: Position[]; transactions: Transaction[]; watchlist: string[] }>(r),
    ),
  addWatchlist: (symbol: string) =>
    fetch('/api/portfolio/watchlist', jsonInit('POST', { symbol })).then((r) => j<{ watchlist: string[] }>(r)),
  removeWatchlist: (symbol: string) =>
    fetch(`/api/portfolio/watchlist/${encodeURIComponent(symbol)}`, { method: 'DELETE' }).then((r) => j<{ watchlist: string[] }>(r)),
  portfolioHistory: (range: string, account = 'all') =>
    fetch(`/api/portfolio/history?range=${range}&account=${encodeURIComponent(account)}`).then((r) => j<PortfolioSnapshot[]>(r)),
  portfolioLive: () => fetch('/api/portfolio/live').then((r) => j<PortfolioLive>(r)),
  /** The whole portfolio, pushed live (SSE). Returns the unsubscribe function. */
  portfolioStream: (onFrame: (p: PortfolioLive) => void, onState?: (open: boolean) => void): (() => void) => {
    const es = new EventSource('/api/portfolio/stream')
    es.addEventListener('portfolio', (ev) => {
      try {
        onFrame(JSON.parse((ev as MessageEvent).data) as PortfolioLive)
      } catch {
        /* malformed frame — ignore */
      }
    })
    es.onopen = () => onState?.(true)
    es.onerror = () => onState?.(false) // EventSource reconnects by itself
    return () => es.close()
  },
  setPosition: (b: {
    symbol: string
    quantity: number
    costBasis: number
    stopLoss?: number | null
    takeProfit?: number | null
  }) =>
    fetch('/api/portfolio/position', jsonInit('POST', b)).then((r) =>
      j<{ position: Position | null; positions: Position[] }>(r),
    ),
  removePosition: (symbol: string) =>
    fetch(`/api/portfolio/position/${encodeURIComponent(symbol)}`, { method: 'DELETE' }).then((r) =>
      j<{ positions: Position[] }>(r),
    ),
  setLevels: (b: { symbol: string; stopLoss?: number | null; takeProfit?: number | null }) =>
    fetch('/api/portfolio/levels', jsonInit('POST', b)).then((r) => j<{ positions: Position[] }>(r)),
  trade: (b: {
    symbol: string
    side: TradeSide
    quantity: number
    price: number
    fee?: number
    note?: string
    stopLoss?: number | null
    takeProfit?: number | null
  }) =>
    fetch('/api/portfolio/trade', jsonInit('POST', b)).then((r) =>
      j<{ positions: Position[]; transaction: Transaction }>(r),
    ),

  quote: (symbol: string) =>
    fetch(`/api/market/quote/${encodeURIComponent(symbol)}`).then((r) => j<Quote>(r)),
  quotes: (symbols: string[]) =>
    fetch(`/api/market/quotes?symbols=${encodeURIComponent(symbols.join(','))}`).then((r) =>
      j<Quote[]>(r),
    ),
  extendedQuotes: (symbols: string[]) =>
    fetch(`/api/market/extended?symbols=${encodeURIComponent(symbols.join(','))}`).then((r) =>
      j<ExtendedQuote[]>(r),
    ),
  liveQuotes: (symbols: string[]) =>
    fetch(`/api/market/live?symbols=${encodeURIComponent(symbols.join(','))}`).then((r) =>
      j<LiveQuote[]>(r),
    ),
  search: (q: string) =>
    fetch(`/api/market/search?q=${encodeURIComponent(q)}`).then((r) => j<SearchResult[]>(r)),
  ohlc: (symbol: string, timeframe: Timeframe, from?: string, to?: string) => {
    const p = new URLSearchParams({ symbol, timeframe })
    if (from) p.set('from', from)
    if (to) p.set('to', to)
    return fetch(`/api/market/ohlc?${p}`).then((r) => j<OhlcBar[]>(r))
  },
  // Real-time 1m bars from the WS (regular + pre/after-market) — the live extended-hours tail.
  liveBars: (symbol: string) =>
    fetch(`/api/market/live-bars?symbol=${encodeURIComponent(symbol)}`).then((r) => j<OhlcBar[]>(r)),
  technicals: (symbol: string) =>
    fetch(`/api/market/technicals/${encodeURIComponent(symbol)}`).then((r) => j<TechnicalAnalysis>(r)),
  profile: (symbol: string) =>
    fetch(`/api/market/profile/${encodeURIComponent(symbol)}`).then((r) => j<CompanyProfile>(r)),

  tasks: () => fetch('/api/tasks').then((r) => j<Task[]>(r)),
  /** The tasks that run inside a chat. */
  chatTasks: (chatId: string) => fetch(`/api/tasks/chat/${encodeURIComponent(chatId)}`).then((r) => j<Task[]>(r)),
  taskLimits: () => fetch('/api/tasks/limits').then((r) => j<TaskLimits>(r)),
  taskUnread: () => fetch('/api/tasks/unread').then((r) => j<{ count: number }>(r)),
  /** A schedule's words, next runs and runs a day (or why it isn't one). */
  taskPreview: (b: { schedule: unknown; created?: number; runs?: number }) => fetch('/api/tasks/preview', jsonInit('POST', b)).then((r) => j<SchedulePreview>(r)),
  createTask: (b: TaskInput) => fetch('/api/tasks', jsonInit('POST', b)).then((r) => j<Task & { warnings?: string[] }>(r)),
  updateTask: (id: string, b: Partial<TaskInput>) =>
    fetch(`/api/tasks/${id}`, jsonInit('PATCH', b)).then((r) => j<Task & { warnings?: string[] }>(r)),
  deleteTask: (id: string) => fetch(`/api/tasks/${id}`, { method: 'DELETE' }).then((r) => j<{ ok: boolean }>(r)),
  /** A task in a chat starts there ({started, chatId}); a report runs and returns its run. */
  runTask: (id: string) => fetch(`/api/tasks/${id}/run`, { method: 'POST' }).then((r) => j<TaskRun | { started: true; chatId: string }>(r)),
  /** Market-data usage today: this server's count by source + EODHD's own counter (admin). */
  eodhdUsage: () =>
    fetch('/api/admin/eodhd-usage').then((r) =>
      j<{
        today: { day: string; total: number; requests: number; byEndpoint: Record<string, number>; bySource: Record<string, number> }
        account: { used: number; limit: number; extra: number; date: string; at: number } | null
      }>(r),
    ),
  /** Every AI run no one typed, and today's budget. */
  wakes: () =>
    fetch('/api/tasks/wakes').then((r) =>
      j<{ budget: { used: number; max: number; unlimited?: boolean }; wakes: { id: string; source: string; sourceId: string; chatId: string | null; title: string; status: string; detail: string; at: number; finishedAt: number | null }[] }>(r),
    ),
  taskRuns: (id: string) => fetch(`/api/tasks/${id}/runs`).then((r) => j<TaskRun[]>(r)),
  tasksMarkRead: () => fetch('/api/tasks/read', { method: 'POST' }).then((r) => j<{ ok: boolean }>(r)),

  telegramTest: () => fetch('/api/telegram/test', { method: 'POST' }).then((r) => j<{ ok: boolean }>(r)),
  telegramDetectChat: () =>
    fetch('/api/telegram/detect-chat', { method: 'POST' }).then((r) => j<{ chatId: string; name: string }>(r)),
  telegramDisconnect: () => fetch('/api/telegram/disconnect', { method: 'POST' }).then((r) => j<{ ok: boolean }>(r)),

  // Browser fetch devices (extension pairing)
  bridgeDevices: () =>
    fetch('/api/bridge/devices').then((r) =>
      j<{ devices: BridgeDevice[]; connectedCount: number }>(r),
    ),
  bridgeAddDevice: (label: string) =>
    fetch('/api/bridge/devices', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ label }),
    }).then((r) => j<{ id: string; token: string; label: string; createdAt: number }>(r)),
  bridgeRevokeDevice: (id: string) =>
    fetch(`/api/bridge/devices/${id}`, { method: 'DELETE' }).then((r) => j<{ ok: boolean }>(r)),
}

export interface BridgeDevice {
  id: string
  label: string
  createdAt: number
  lastSeenAt: number | null
  connected: boolean
}

export interface Skill {
  id: string
  name: string
  instructions: string
  enabled: boolean
}
export interface McpServer {
  id: string
  label: string
  url: string
  allowedTools: string[]
  enabled: boolean
}

const jbody = (m: string, b: unknown) => ({ method: m, headers: { 'content-type': 'application/json' }, body: JSON.stringify(b) })

export const integrations = {
  skills: () => fetch('/api/skills').then((r) => j<Skill[]>(r)),
  skillCreate: (name: string, instructions: string) => fetch('/api/skills', jbody('POST', { name, instructions })).then((r) => j<Skill>(r)),
  skillUpdate: (id: string, p: Partial<Pick<Skill, 'name' | 'instructions' | 'enabled'>>) => fetch(`/api/skills/${id}`, jbody('PATCH', p)).then((r) => j<{ ok: boolean }>(r)),
  skillDelete: (id: string) => fetch(`/api/skills/${id}`, { method: 'DELETE' }).then((r) => j<{ ok: boolean }>(r)),

  mcp: () => fetch('/api/mcp').then((r) => j<McpServer[]>(r)),
  mcpCreate: (label: string, url: string, allowedTools: string) => fetch('/api/mcp', jbody('POST', { label, url, allowedTools })).then((r) => j<McpServer>(r)),
  mcpUpdate: (id: string, p: { label?: string; url?: string; allowedTools?: string; enabled?: boolean }) => fetch(`/api/mcp/${id}`, jbody('PATCH', p)).then((r) => j<{ ok: boolean }>(r)),
  mcpDelete: (id: string) => fetch(`/api/mcp/${id}`, { method: 'DELETE' }).then((r) => j<{ ok: boolean }>(r)),
}

/* ── broker (IBKR) — admin only ───────────────────────────────────────── */

export const brokerApi = {
  status: (broker: BrokerName = 'ibkr') => fetch(`/api/broker/status?broker=${broker}`).then((r) => j<BrokerStatus>(r)),
  snapshot: (broker: BrokerName = 'ibkr') => fetch(`/api/broker/snapshot?broker=${broker}`).then((r) => j<BrokerSnapshot>(r)),
  /** Write-only: the password travels once over HTTPS and is sealed server-side. */
  saveCredentials: (username: string, password: string, mode: BrokerMode) =>
    fetch('/api/broker/credentials', jsonInit('PUT', { username, password, mode })).then((r) => j<BrokerStatus>(r)),
  deleteCredentials: () => fetch('/api/broker/credentials', { method: 'DELETE' }).then((r) => j<BrokerStatus>(r)),
  startGateway: () => fetch('/api/broker/gateway/start', { method: 'POST' }).then((r) => j<BrokerStatus>(r)),
  stopGateway: () => fetch('/api/broker/gateway/stop', { method: 'POST' }).then((r) => j<BrokerStatus>(r)),
  placeOrder: (o: PlaceOrderRequest, broker: BrokerName = 'ibkr') =>
    fetch(`/api/broker/orders?broker=${broker}`, jsonInit('POST', o)).then((r) => j<{ ok: boolean; orderId: number }>(r)),
  cancelOrder: (orderId: number) =>
    fetch(`/api/broker/orders/${orderId}`, { method: 'DELETE' }).then((r) => j<{ ok: boolean }>(r)),
  /** Live snapshot stream (SSE, same-origin cookie auth). Returns an unsubscribe fn. */
  subscribe: (onSnapshot: (s: BrokerSnapshot) => void, onState?: (open: boolean) => void, broker: BrokerName = 'ibkr'): (() => void) => {
    const es = new EventSource(`/api/broker/stream?broker=${broker}`)
    es.addEventListener('snapshot', (ev) => {
      try {
        onSnapshot(JSON.parse((ev as MessageEvent).data) as BrokerSnapshot)
      } catch {
        /* malformed frame — ignore */
      }
    })
    es.onopen = () => onState?.(true)
    es.onerror = () => onState?.(false) // EventSource auto-reconnects
    return () => es.close()
  },
}

/* ── trade workers ──────────────────────────────────────────────────── */

// One EventSource for every worker view on the page; views filter by id.
let workerEs: EventSource | null = null
const workerListeners = new Set<(id: string) => void>()
let userEs: EventSource | null = null
const userListeners = new Set<(e: UserEvent) => void>()

export interface XaiLoginView {
  sessionId: string
  userCode: string
  verificationUrl: string
  verificationUrlComplete: string
  expiresAt: number
  interval: number
  state: 'pending' | 'connected' | 'denied' | 'expired' | 'error' | 'cancelled'
  message: string | null
}
export interface XaiUsageSplit {
  runs: number
  input: number
  output: number
  costUsd: number
}
export interface XaiSubscriptionStatus {
  connected: boolean
  status: 'active' | 'needs_login' | 'tier_denied' | 'not_connected'
  email: string | null
  name: string | null
  connectedAt: number | null
  lastRefresh: number | null
  exhaustedAt: number | null
  lastError: string | null
  fallbackToApi: boolean
  week: { api: XaiUsageSplit; subscription: XaiUsageSplit }
  pending: XaiLoginView | null
}

let orderEs: EventSource | null = null
const orderListeners = new Set<(id: string) => void>()

/** Chat orders ("tickets"): single IBKR orders drafted from chat, approved on their card. */
export const ordersApi = {
  list: () => fetch('/api/orders').then((r) => j<TicketRecord[]>(r)),
  /** Compile an order filled in on the ticket: a draft and its system-computed card. Places nothing. */
  draft: (spec: unknown) => fetch('/api/orders/draft', jsonInit('POST', { spec })).then((r) => j<TicketRecord>(r)),
  get: (id: string) => fetch(`/api/orders/${encodeURIComponent(id)}`).then((r) => j<TicketRecord>(r)),
  /** Send exactly the order the user reviewed — the server refuses if the hash or the position changed. */
  approve: (id: string, planHash: string) => fetch(`/api/orders/${encodeURIComponent(id)}/approve`, jsonInit('POST', { planHash })).then((r) => j<TicketRecord>(r)),
  reject: (id: string) => fetch(`/api/orders/${encodeURIComponent(id)}/reject`, { method: 'POST' }).then((r) => j<{ ok: boolean }>(r)),
  cancel: (id: string) => fetch(`/api/orders/${encodeURIComponent(id)}/cancel`, { method: 'POST' }).then((r) => j<{ ok: boolean; error?: string }>(r)),
  /** Notified with a ticket id whenever that ticket changes. Returns the unsubscribe fn. */
  subscribe: (onChange: (id: string) => void): (() => void) => {
    orderListeners.add(onChange)
    if (!orderEs) {
      orderEs = new EventSource('/api/orders/stream')
      orderEs.addEventListener('change', (ev) => {
        try {
          const { id } = JSON.parse((ev as MessageEvent).data) as { id: string }
          for (const l of orderListeners) l(id)
        } catch {
          /* malformed frame — ignore */
        }
      })
    }
    return () => {
      orderListeners.delete(onChange)
      if (!orderListeners.size) {
        orderEs?.close()
        orderEs = null
      }
    }
  },
}

/** The user's own Grok subscription (xAI device-code login). */
export const xaiApi = {
  status: () => fetch('/api/xai-oauth/status').then((r) => j<XaiSubscriptionStatus>(r)),
  start: () => fetch('/api/xai-oauth/start', { method: 'POST' }).then((r) => j<XaiLoginView>(r)),
  poll: (id: string) => fetch(`/api/xai-oauth/poll/${encodeURIComponent(id)}`).then((r) => j<XaiLoginView>(r)),
  cancel: (id: string) => fetch(`/api/xai-oauth/session/${encodeURIComponent(id)}`, { method: 'DELETE' }).then((r) => j<{ ok: boolean }>(r)),
  disconnect: () => fetch('/api/xai-oauth/disconnect', { method: 'POST' }).then((r) => j<{ ok: boolean }>(r)),
  setFallback: (fallbackToApi: boolean) => fetch('/api/xai-oauth/settings', jsonInit('PUT', { fallbackToApi })).then((r) => j<{ ok: boolean }>(r)),
}

import type { BrokerName } from '@shared/brokers'

export interface RobinhoodStatus {
  connected: boolean
  status: 'active' | 'needs_login' | 'not_connected'
  connectedAt: number | null
  lastRefresh: number | null
  toolCount: number | null
  lastError: string | null
  session: 'disconnected' | 'connecting' | 'connected'
  account: string | null
  tradingBlockedReason: string | null
}
export const robinhoodApi = {
  status: () => fetch('/api/broker/robinhood/status').then((r) => j<RobinhoodStatus>(r)),
  start: () => fetch('/api/broker/robinhood/start', { method: 'POST' }).then((r) => j<{ url: string }>(r)),
  finish: (url: string) => fetch('/api/broker/robinhood/finish', jsonInit('POST', { url })).then((r) => j<{ ok: boolean }>(r)),
  tools: () => fetch('/api/broker/robinhood/tools?fresh=1').then((r) => j<{ tools: { name: string; description: string }[] }>(r)),
  disconnect: () => fetch('/api/broker/robinhood/disconnect', { method: 'POST' }).then((r) => j<{ ok: boolean }>(r)),
}

export const workersApi = {
  list: () => fetch('/api/workers').then((r) => j<WorkerRecord[]>(r)),
  /** Compile a plan filled in on the ticket into a worker draft with its approval card. Places nothing. */
  draft: (spec: unknown) => fetch('/api/workers/draft', jsonInit('POST', { spec })).then((r) => j<WorkerDetail>(r)),
  get: (id: string) => fetch(`/api/workers/${encodeURIComponent(id)}`).then((r) => j<WorkerDetail>(r)),
  /** Approve exactly the plan the user reviewed — the server refuses if the hash changed. */
  approve: (id: string, planHash: string) =>
    fetch(`/api/workers/${encodeURIComponent(id)}/approve`, jsonInit('POST', { planHash })).then((r) => j<WorkerDetail>(r)),
  reject: (id: string) => fetch(`/api/workers/${encodeURIComponent(id)}/reject`, { method: 'POST' }).then((r) => j<{ ok: boolean }>(r)),
  close: (id: string) => fetch(`/api/workers/${encodeURIComponent(id)}/close`, { method: 'POST' }).then((r) => j<{ ok: boolean }>(r)),
  /** A change to a running worker: the server returns its change card (a draft to approve). */
  revise: (id: string, spec: unknown) =>
    fetch(`/api/workers/${encodeURIComponent(id)}/revise`, jsonInit('POST', { spec })).then((r) => j<WorkerDetail>(r)),
  resume: (id: string) => fetch(`/api/workers/${encodeURIComponent(id)}/resume`, { method: 'POST' }).then((r) => j<{ ok: boolean }>(r)),
  killAll: () => fetch('/api/workers/kill-all', { method: 'POST' }).then((r) => j<{ stopped: number }>(r)),
  /** Worker groups: several workers approved on one card (one-cancels-others, pairs, combined P&L rules). */
  groups: () => fetch('/api/workers/groups').then((r) => j<GroupRecord[]>(r)),
  group: (id: string) => fetch(`/api/workers/groups/${encodeURIComponent(id)}`).then((r) => j<GroupDetail>(r)),
  approveGroup: (id: string, planHash: string) =>
    fetch(`/api/workers/groups/${encodeURIComponent(id)}/approve`, jsonInit('POST', { planHash })).then((r) => j<GroupDetail>(r)),
  rejectGroup: (id: string) => fetch(`/api/workers/groups/${encodeURIComponent(id)}/reject`, { method: 'POST' }).then((r) => j<{ ok: boolean }>(r)),
  closeGroup: (id: string) => fetch(`/api/workers/groups/${encodeURIComponent(id)}/close`, { method: 'POST' }).then((r) => j<{ ok: boolean }>(r)),
  /** The trade journal: every trade, how each lot exited — and "what if" on the same bars. */
  journal: () => fetch('/api/workers/journal').then((r) => j<{ trades: JournalTrade[]; stats: JournalStats }>(r)),
  excursion: (id: string) => fetch(`/api/workers/journal/${encodeURIComponent(id)}/excursion`).then((r) => j<TradeExcursion | { error: string }>(r)),
  whatIf: (ids?: string[]) => fetch('/api/workers/journal/whatif', jsonInit('POST', { ids })).then((r) => j<{ trades: WhatIfTrade[]; summary: WhatIfSummary[] }>(r)),
  limits: () => fetch('/api/workers/limits').then((r) => j<WorkerLimits>(r)),
  setLimits: (l: WorkerLimits) => fetch('/api/workers/limits', jsonInit('PUT', l)).then((r) => j<WorkerLimits>(r)),
  /** Notified with a worker id whenever that worker changes. Returns the unsubscribe fn. */
  subscribe: (onChange: (id: string) => void): (() => void) => {
    workerListeners.add(onChange)
    if (!workerEs) {
      workerEs = new EventSource('/api/workers/stream')
      workerEs.addEventListener('change', (ev) => {
        try {
          const { id } = JSON.parse((ev as MessageEvent).data) as { id: string }
          for (const l of workerListeners) l(id)
        } catch {
          /* malformed frame — ignore */
        }
      })
    }
    return () => {
      workerListeners.delete(onChange)
      if (!workerListeners.size) {
        workerEs?.close()
        workerEs = null
      }
    }
  },
}

/** Server pushes for this user (an automation woke the AI in a chat, tasks changed). Returns the unsubscribe fn. */
export function subscribeUserEvents(on: (e: UserEvent) => void): () => void {
  userListeners.add(on)
  if (!userEs) {
    userEs = new EventSource('/api/events')
    userEs.addEventListener('event', (ev) => {
      try {
        const e = JSON.parse((ev as MessageEvent).data) as UserEvent
        for (const l of userListeners) l(e)
      } catch {
        /* malformed frame — ignore */
      }
    })
  }
  return () => {
    userListeners.delete(on)
    if (!userListeners.size) {
      userEs?.close()
      userEs = null
    }
  }
}

/** Watchers and scans — workers that never trade. */
export const watchersApi = {
  list: () => fetch('/api/watchers').then((r) => j<WatcherRecord[]>(r)),
  get: (id: string) => fetch(`/api/watchers/${encodeURIComponent(id)}`).then((r) => j<WatcherDetail>(r)),
  setStatus: (id: string, status: WatcherStatus) => fetch(`/api/watchers/${encodeURIComponent(id)}/status`, jsonInit('POST', { status })).then((r) => j<WatcherDetail>(r)),
  runNow: (id: string) => fetch(`/api/watchers/${encodeURIComponent(id)}/run`, { method: 'POST' }).then((r) => j<{ ok: boolean }>(r)),
  remove: (id: string) => fetch(`/api/watchers/${encodeURIComponent(id)}`, { method: 'DELETE' }).then((r) => j<{ ok: boolean }>(r)),
}
